// ============================================================================
// File: CuFleetPanel.tsx — Spec 160 T015
// Author: Peter Dobler | Created: 2026-05-31
// Feature: 160 - Dashboard Redesign (US1 — Admin Dashboard)
// Constitution: v1.15.0 | Principle XXXVII
// ============================================================================
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ComposedChart, Area, Line, XAxis, ResponsiveContainer, Tooltip,
} from 'recharts';
import { DashboardPanel, Badge, Spinner, chartTooltipStyle, AppTooltip, ErrorState } from '@/components/ui';
import type { DashboardKpis } from '../../../../../shared/types/dashboard';

interface CuFleetPanelProps {
  data: DashboardKpis['cuFleet'] | undefined;
  loading?: boolean;
}

export const CuFleetPanel: React.FC<CuFleetPanelProps> = ({ data, loading = false }) => {
  // Null when nothing was measured in the window (Spec 286 T160 item 5): shown as "no data", never an idle 0%.
  const avg24h = data?.avgUtilization24hPct ?? null;
  const healthColor: 'error' | 'warning' | 'success' | 'default' =
    avg24h === null ? 'default' : avg24h > 80 ? 'error' : avg24h > 60 ? 'warning' : 'success';
  // Peak broken down by Subject Matter Area (replaces the single fleet-wide absolute max).
  const peakBySma = data?.peakBySma ?? [];

  // 60-day trend: fleet Avg area + a PEAK line per SMA (replaces the single fleet-max line).
  // Line set = SMAs with recent activity (peakBySma), else whatever appears in the daily peaks.
  const trend60d = data?.trend60d ?? [];
  const smaNames = peakBySma.length > 0
    ? peakBySma.map(s => s.sma)
    : Array.from(new Set(trend60d.flatMap(d => Object.keys(d.smaPeaks ?? {}))));
  const SMA_LINE_COLORS = ['#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#06b6d4', '#ec4899', '#eab308'];
  const smaColor = (i: number) => SMA_LINE_COLORS[i % SMA_LINE_COLORS.length];
  // Flatten each day's per-SMA peaks onto the point so recharts can read dataKey={sma}.
  // Spec 215 US1(b): carry the fleet peak too — computed since Spec 160, rendered at last.
  const trend = trend60d.map(d => ({ date: d.date, avgPct: d.avgPct, fleetMaxPct: d.maxPct, ...(d.smaPeaks ?? {}) }));
  const max24h = data?.maxUtilization24hPct ?? null;

  return (
    <DashboardPanel
      title="CU Utilization — Fleet"
      titleInfo="Fleet-wide Fabric capacity-unit (CU) utilization from streaming telemetry: the 24h average + peak, peaks attributed to each subject-matter-area, and a 60-day daily trend. Above 100% = throttling."
      actions={
        <Link to="/operations/telemetry?tab=live" className="text-xs font-medium text-[var(--color-accent)] hover:underline">
          View telemetry →
        </Link>
      }
    >
      {loading ? (
        <div className="flex min-h-[100px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title="Could not read the CU figures" />
      ) : (
        <div className="flex h-full flex-col">
          <AppTooltip
            title="Fleet CU utilization over the last 24 hours — the big number is the fleet average, the second is the single highest capacity reading (peaks above 100% mean a capacity was throttling)."
            placement="top" className="block"
          >
          <div className="mb-1 flex items-baseline gap-2">
            {avg24h === null ? (
              <span className="text-lg font-bold leading-none text-[var(--color-text-secondary)]">no data</span>
            ) : (
              <span className="text-[length:var(--text-display)] font-bold leading-none text-[var(--color-text-primary)]">
                {avg24h.toFixed(1)}%
              </span>
            )}
            <Badge variant={healthColor} isOutlined>avg 24h</Badge>
            {/* fleet 24h MAX — always computed, now finally rendered. */}
            <span className={`text-lg font-bold leading-none ${max24h === null ? 'text-[var(--color-text-secondary)]' : max24h >= 100 ? 'text-[var(--color-status-danger)]' : max24h >= 80 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-text-secondary)]'}`}>
              {max24h === null ? 'no data' : `${max24h.toFixed(1)}%`}
            </span>
            <Badge variant={max24h !== null && max24h >= 80 ? 'warning' : 'default'} isOutlined>max 24h</Badge>
          </div>
          </AppTooltip>

          {/* Peak 24h broken down by Subject Matter Area (owner directive: attribute the
              peak to an SMA, not one fleet-wide absolute max). Empty list ⇒ no telemetry in
              the 24h window (stale collection) — shown explicitly so 0 isn't mistaken for healthy. */}
          <div className="mt-2">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">Peak 24h by SMA</div>
            {peakBySma.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {peakBySma.map((s) => {
                  const tone = s.peakPct >= 100
                    ? 'text-[var(--color-status-danger)]'
                    : s.peakPct >= 80
                      ? 'text-[var(--color-status-warning)]'
                      : 'text-[var(--color-text-primary)]';
                  return (
                    <AppTooltip
                      key={s.sma}
                      title={`${s.sma}: peak ${s.peakPct.toFixed(1)}% CU across ${s.capacities} capacity(ies) in the last 24h. Click to view live telemetry.`}
                      placement="top"
                    >
                    <Link
                      to="/operations/telemetry?tab=live"
                      className="flex items-center gap-1.5 rounded-[var(--radius-md)] border border-transparent bg-[var(--color-bg-subtle)] px-2 py-1 transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-bg-card)]"
                    >
                      <span className="text-[11px] text-[var(--color-text-secondary)]">{s.sma}</span>
                      <span className={`text-sm font-bold leading-none ${tone}`}>{s.peakPct.toFixed(1)}%</span>
                    </Link>
                    </AppTooltip>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-[var(--color-text-secondary)]">No telemetry in the last 24h (collection may be stale)</div>
            )}
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2">
            {[
              { label: 'Above 80%', value: data?.capacitiesAbove80 ?? 0, danger: (data?.capacitiesAbove80 ?? 0) > 0, to: '/operations/telemetry?tab=live', tip: 'Capacities whose CU utilization exceeded 80% in the last 24h (running hot). Click for live per-capacity CU.' },
              { label: 'Throttling 24h', value: data?.throttlingWindows24h ?? 0, danger: (data?.throttlingWindows24h ?? 0) > 0, to: '/operations/telemetry?tab=live', tip: 'Number of throttling windows recorded across the fleet in the last 24h. Click to view throttling in live telemetry.' },
              { label: 'Paused', value: data?.pausedCount ?? 0, to: '/capacity-metrics', tip: 'Capacities currently paused/suspended (they collect no CU telemetry). Click to view capacities.' },
            ].map((m) => (
              <AppTooltip key={m.label} title={m.tip} placement="top" className="block">
              <Link
                to={m.to}
                className="block rounded-[var(--radius-md)] border border-transparent bg-[var(--color-bg-subtle)] px-2 py-1.5 text-center transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-bg-card)]"
              >
                <div className={`text-base font-bold leading-tight ${m.danger ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-text-primary)]'}`}>
                  {m.value}
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">{m.label}</div>
              </Link>
              </AppTooltip>
            ))}
          </div>

          <hr className="my-2 border-[var(--color-border)]" />

          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">60-day trend — peak by SMA</span>
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-[10px] text-[var(--color-text-secondary)]">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#6366f1' }} />Avg
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: '#94a3b8' }} />Fleet max
              </span>
              {smaNames.map((sma, i) => (
                <span key={sma} className="flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: smaColor(i) }} />{sma}
                </span>
              ))}
            </div>
          </div>
          <div className="min-h-[130px] flex-1">
            {trend.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend}>
                  <defs>
                    <linearGradient id="cuGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" hide />
                  <Tooltip
                    {...chartTooltipStyle}
                    formatter={((v: unknown, name: unknown) => [`${Number(v).toFixed(1)}%`, String(name)]) as any}
                    labelFormatter={(l: unknown) => String(l)}
                  />
                  <Area
                    type="monotone"
                    dataKey="avgPct"
                    name="Avg"
                    stroke="#6366f1"
                    fill="url(#cuGrad)"
                    dot={false}
                    strokeWidth={2}
                  />
                  {/* One PEAK line per SMA (replaces the single fleet-max peak line). */}
                  {smaNames.map((sma, i) => (
                    <Line
                      key={sma}
                      type="monotone"
                      dataKey={sma}
                      name={sma}
                      stroke={smaColor(i)}
                      fill="none"
                      dot={false}
                      strokeWidth={1.5}
                      connectNulls
                    />
                  ))}
                  {/* Spec 215 US1(b): fleet-peak line (dashed, muted). Rendered LAST so it paints
                      ON TOP of the per-SMA peak lines — the fleet max equals the highest SMA peak
                      each day, so if drawn first the coincident (e.g. DDS) SMA line occludes it and
                      it looks like the max series ends early. Drawn last, it reaches today. */}
                  <Line
                    type="monotone"
                    dataKey="fleetMaxPct"
                    name="Fleet max"
                    stroke="#94a3b8"
                    strokeDasharray="4 3"
                    fill="none"
                    dot={false}
                    strokeWidth={1.5}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-[var(--color-text-secondary)]">No trend data</span>
            )}
          </div>
        </div>
      )}
    </DashboardPanel>
  );
};

export default CuFleetPanel;
